<script setup lang="ts">
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DataTable, { type DataTableFilterEvent, type DataTableRowClickEvent } from 'primevue/datatable';
import type { DataTableFilterMeta } from 'primevue/datatable';
import { FilterMatchMode } from '@primevue/core/api';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import MultiSelect from '@apparatix/library/MultiSelect.vue';
import ToggleSwitch from 'primevue/toggleswitch';
import type { MenuItem } from 'primevue/menuitem';
import Badge from 'primevue/badge';
import Avatar from 'primevue/avatar';
import FileExportIcon from '@primeicons/vue/file-export';
import FilterSlashIcon from '@primeicons/vue/filter-slash';
import BriefcaseIcon from '@primeicons/vue/briefcase';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';
import AppContainer from '@apparatix/library/AppContainer.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';
import HeightOptimizer from './HeightOptimizer.vue';
import OfferHoldDialog from './OfferHoldDialog.vue';
import OfferHoldHistoryDialog from './OfferHoldHistoryDialog.vue';
import {
  dateFilterMatchModeOptions,
  numericFilterMatchModeOptions,
  useBuildDefaultColumns,
  useColumnConfig,
  useColumnFilters,
  useKeepAliveActive,
  useLocalStorage,
  useOfferManagement,
  useOpportunities,
  useToastNotifications,
} from '@apparatix/composables';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';
import type { OfferHoldHistoryEntry, OfferManagementRow, OfferManagementSettings } from '@apparatix/types/OfferManagement';

type FilterType = 'text' | 'number' | 'date' | 'select';

type ColumnFilterConfig = {
  type: FilterType;
  placeholder: string;
  width: string;
  /** Appended to end-of-range dates on `Y-m-d H:i` columns so they filter by day. */
  dateUpperBoundSuffix?: string;
};

const route = useRoute();
const router = useRouter();
const { isKeepAliveActive } = useKeepAliveActive();
const {
  isLoadingHoldHistory, isSavingHoldThru, loadHoldHistory, loadOffers, updateHoldThru,
} = useOfferManagement();
const { loadOpportunityOptions } = useOpportunities();
const { showSuccess } = useToastNotifications();
const { getItem: getStoredItem, setItem: setStoredItem } = useLocalStorage();
const {
  dateBetweenValue,
  dateFilterValue,
  numericBetweenValue,
  numericFilterValue,
  onDateBetweenFilterInput,
  onDateFilterInput,
  onNumericBetweenFilterInput,
  onNumericFilterInput,
  resetFilterValueOnMatchModeChange,
} = useColumnFilters();
const dt = ref();
const rows = ref<OfferManagementRow[]>([]);
const settings = ref<OfferManagementSettings>({ canEditHoldThru: false });
const loading = ref(false);
const showFiltersKey = 'crm:offer-management:show-filters';
const showFilters = ref(getStoredItem<boolean>(showFiltersKey) ?? true);
const filteredCount = ref(0);
const selectedOffer = ref<OfferManagementRow | null>(null);
const showHoldDialog = ref(false);
const showHistoryDialog = ref(false);
const holdHistory = ref<OfferHoldHistoryEntry[]>([]);
const previousTableFilters = ref<DataTableFilterMeta>();
const maintenanceMenuRef = ref();

const defaultColumns = useBuildDefaultColumns([
  ['proposalId', 'Prop Id'], ['createdBy', 'Created By'], ['currentAe', 'Current AE'], ['advertiser', 'Advertiser'],
  ['advertiserId', 'Advertiser ID', false],
  ['createdDate', 'Created'], ['holdThru', 'Hold Thru'], ['holdAge', 'Hold Age'], ['startDate', 'Start'],
  ['length', 'Length'], ['documentState', 'Document State', false], ['proposalPrinted', 'Proposal Printed', false],
  ['offerPrinted', 'Offer Printed'], ['opportunityName', 'Opportunity Name'], ['documentStatus', 'Document Status'],
  ['faceCount', 'Face Count'], ['holdCount', 'Hold Count'], ['faces', 'Faces'],
]);
const {
  handleColumnOrderChange, handleVisibleColumnChange, visibleColumns, columnOrder,
} = useColumnConfig({
  visibleItemsKey: 'crm:offer-management:visible-columns',
  columnOrderKey: 'crm:offer-management:column-order',
  ref: dt,
  defaultColumns,
});

const columnFilterConfig: Record<string, ColumnFilterConfig> = {
  proposalId: { type: 'number', placeholder: 'Prop Ids', width: '110px' },
  createdBy: { type: 'select', placeholder: 'Creators', width: '150px' },
  currentAe: { type: 'select', placeholder: 'AEs', width: '150px' },
  advertiser: { type: 'text', placeholder: 'Advertisers', width: '220px' },
  advertiserId: { type: 'number', placeholder: 'Adv Ids', width: '110px' },
  createdDate: {
    type: 'date', placeholder: 'Created', width: '150px', dateUpperBoundSuffix: ' 23:59',
  },
  holdThru: { type: 'date', placeholder: 'Hold Thru', width: '150px' },
  holdAge: { type: 'number', placeholder: 'Days', width: '110px' },
  startDate: { type: 'date', placeholder: 'Starts', width: '150px' },
  length: { type: 'select', placeholder: 'Lengths', width: '130px' },
  documentState: { type: 'select', placeholder: 'States', width: '140px' },
  proposalPrinted: { type: 'date', placeholder: 'Prop Printed', width: '150px' },
  offerPrinted: { type: 'date', placeholder: 'Offer Printed', width: '150px' },
  opportunityName: { type: 'text', placeholder: 'Opportunities', width: '220px' },
  documentStatus: { type: 'select', placeholder: 'Statuses', width: '150px' },
  faceCount: { type: 'number', placeholder: 'Count', width: '110px' },
  holdCount: { type: 'number', placeholder: 'Holds', width: '110px' },
  faces: { type: 'text', placeholder: 'Inv Numbers', width: '240px' },
};
const filterTypeFor = (field: string): FilterType => columnFilterConfig[field]?.type ?? 'text';
const hasFilterMenu = (field: string) => ['number', 'date'].includes(filterTypeFor(field));
const matchModeOptionsFor = (field: string) => {
  const type = filterTypeFor(field);
  if (type === 'number') return numericFilterMatchModeOptions;
  if (type === 'date') return dateFilterMatchModeOptions;
  return undefined;
};
const defaultMatchModeFor = (field: string): string => ({
  select: FilterMatchMode.IN,
  number: FilterMatchMode.EQUALS,
  date: FilterMatchMode.CONTAINS,
  text: FilterMatchMode.CONTAINS,
})[filterTypeFor(field)];
const multiModeFilterFields = defaultColumns.map(column => column.field).filter(hasFilterMenu);

const selectFilterFields = defaultColumns.map(column => column.field).filter(field => filterTypeFor(field) === 'select');
const distinctSorted = (values: (string | null)[]): string[] => [...new Set(values)].filter((value): value is string => Boolean(value)).sort();
const filterOptionsByField = computed<Record<string, string[]>>(() => Object.fromEntries(selectFilterFields.map(field => [
  field,
  distinctSorted(rows.value.map(row => row[field as keyof OfferManagementRow] as string | null)),
])));

// Pairing `width` with `max-width` keeps long unbroken content from stretching a column past its
// width (same approach as ExpirationsHome). Resized widths are saved by ColumnDisplaySelector.
const columnStyle = (field: string) => {
  const width = columnFilterConfig[field]?.width ?? '150px';
  return { width, maxWidth: width };
};

const defaultFilters = computed<DataTableFilterMeta>(() => Object.fromEntries(defaultColumns.map(column => [
  column.field,
  { value: null, matchMode: defaultMatchModeFor(column.field) },
])));
const tableFilters = ref<DataTableFilterMeta>({ ...defaultFilters.value });
const isFilterChanged = computed(() => JSON.stringify(tableFilters.value) !== JSON.stringify(defaultFilters.value));
const isColumnShown = (field: string) => visibleColumns.value.includes(field);
const rowClass = () => 'cursor-pointer';
const context = computed<Record<string, string>>(() => {
  const keys = ['offers', 'office', 'ae'];
  return Object.fromEntries(keys.flatMap(key => typeof route.query[key] === 'string' ? [[key, route.query[key]]] : []));
});

resetFilterValueOnMatchModeChange(tableFilters, multiModeFilterFields);

const loadHome = async () => {
  if (loading.value) return;
  loading.value = true;
  const response = await loadOffers(context.value);
  if (response) {
    rows.value = response.offers;
    settings.value = response.settings;
    filteredCount.value = response.offers.length;
  }
  loading.value = false;
};

const onFilter = (event: DataTableFilterEvent) => {
  filteredCount.value = event.filteredValue?.length ?? rows.value.length;
};

const clearFilters = () => tableFilters.value = { ...defaultFilters.value };

const onShowFiltersChange = (value: boolean) => {
  setStoredItem({ key: showFiltersKey, value });

  if (!value) {
    previousTableFilters.value = { ...tableFilters.value };
    tableFilters.value = undefined;
    filteredCount.value = rows.value.length;
    return;
  }

  tableFilters.value = previousTableFilters.value ?? { ...defaultFilters.value };
  previousTableFilters.value = undefined;
};

const onRowClick = (event: DataTableRowClickEvent) => {
  selectedOffer.value = event.data as OfferManagementRow;
  showHoldDialog.value = true;
};

const closeHoldDialog = () => {
  showHoldDialog.value = false;
};

const openOpportunityFlyout = async () => {
  const opportunityId = selectedOffer.value?.opportunityId;
  if (!opportunityId) return;

  closeHoldDialog();
  await loadOpportunityOptions();
  await router.push({
    path: `/crm/offer-management/detail/opportunity/${opportunityId}`,
    query: route.query,
  });
};

const onSaveHoldThru = async (holdThru: string | null) => {
  const row = selectedOffer.value;
  if (!row) return;

  const response = await updateHoldThru(row.proposalId, holdThru, context.value);
  if (!response) return;

  const updatedRow = response.offer ?? { ...row, holdThru: response.holdThru };
  rows.value = rows.value.map(offer => (offer.proposalId === row.proposalId ? updatedRow : offer));
  selectedOffer.value = updatedRow;
  showSuccess(response.holdThru ? 'Hold Thru updated' : 'Hold cleared');
  closeHoldDialog();
};

const viewHoldHistory = async () => {
  const row = selectedOffer.value;
  if (!row) return;

  holdHistory.value = [];
  showHistoryDialog.value = true;
  holdHistory.value = await loadHoldHistory(row.proposalId, context.value) ?? [];
};

const closeHistoryDialog = () => {
  showHistoryDialog.value = false;
};

const viewForm = () => {
  if (!selectedOffer.value) return;
  window.open(`/apx/proposalForm/index.html?pid=${selectedOffer.value.proposalId}`, 'PropGrid');
};

// Clear Hold is hidden for read-only accounts, the same as the hold dialog's Save/Clear buttons.
const maintenanceMenuOptions = computed<MenuItem[]>(() => [
  {
    label: 'Maintenance',
    items: [
      { label: 'View Form', command: viewForm },
      { label: 'View Opportunity', command: openOpportunityFlyout, disabled: !selectedOffer.value?.opportunityId },
      { label: 'View Hold History', command: viewHoldHistory },
      {
        label: 'Clear Hold',
        command: () => onSaveHoldThru(null),
        visible: settings.value.canEditHoldThru,
        disabled: isSavingHoldThru.value || !selectedOffer.value?.holdThru,
      },
    ],
  },
]);

const onMaintenanceClick = (row: OfferManagementRow, event: MouseEvent) => {
  selectedOffer.value = row;
  maintenanceMenuRef.value.toggle(event);
};

const exportCsv = () => dt.value.exportCSV();

onMounted(() => {
  loadHome();
  window.addEventListener(CustomEventNames.crm.offerManagement.reloadOfferManagementHome, loadHome);
});

watch(() => JSON.stringify(context.value), () => loadHome());

onBeforeUnmount(() => window.removeEventListener(CustomEventNames.crm.offerManagement.reloadOfferManagementHome, loadHome));
</script>

<template>
  <AppContainer>
    <FormSkeleton v-if="loading" />
    <template v-else>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-2">
          <Avatar size="normal" shape="circle" style="background-color: var(--p-orange-100);">
            <template #icon>
              <BriefcaseIcon color="var(--p-orange-500)" />
            </template>
          </Avatar>
          <h1 class="text-lg">
            Offer Management
          </h1>
          <Badge :value="filteredCount" size="large" severity="secondary" />
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:gap-4">
          <Button v-tooltip.left="'Export'" severity="secondary" @click="exportCsv">
            <FileExportIcon />
            <span style="font-weight:700;">
              Export
            </span>
          </Button>
        </div>
      </div>
      <div class="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-center gap-2">
          <span class="text-center">Filters</span>
          <ToggleSwitch v-model="showFilters" @update:model-value="value => onShowFiltersChange(value)" />
          <Button
            v-tooltip.left="'Clear Filters'"
            size="small"
            outlined
            severity="secondary"
            class="flex items-center"
            :disabled="!showFilters || !isFilterChanged"
            @click="clearFilters"
          >
            <FilterSlashIcon /><span class="font-bold">Clear Filters</span>
          </Button>
        </div>
        <div class="flex items-center justify-center gap-2 flex-nowrap">
          <ColumnDisplaySelector
            :default-table-columns="defaultColumns"
            :default-visible-columns="defaultColumns.filter(column => column.visible).map(column => column.field)"
            :table-columns="columnOrder"
            :visible-columns="visibleColumns"
            storage-prefix="crm:offer-management"
            :data-table="dt"
            @update:visible-columns="handleVisibleColumnChange"
            @update:table-columns="handleColumnOrderChange"
          />
        </div>
      </div>
      <HeightOptimizer>
        <template #default="{ availableHeight }">
          <DataTable
            ref="dt"
            v-model:filters="tableFilters"
            :value="rows"
            :filter-display="showFilters ? 'row' : undefined"
            data-key="proposalId"
            scrollable
            :scroll-height="availableHeight"
            reorderable-columns
            resizable-columns
            :virtual-scroller-options="{ itemSize: 50 }"
            :row-class="rowClass"
            @filter="onFilter"
            @row-click="onRowClick"
          >
            <Column
              v-for="column in columnOrder"
              :key="column.field"
              :field="column.field"
              :header="column.header"
              :hidden="!isColumnShown(column.field)"
              :reorderable-column="false"
              :show-filter-menu="hasFilterMenu(column.field)"
              :filter-match-mode-options="matchModeOptionsFor(column.field)"
              sortable
              :style="columnStyle(column.field)"
            >
              <template #filter="{ filterModel, filterCallback }">
                <MultiSelect
                  v-if="filterTypeFor(column.field) === 'select'"
                  v-show="showFilters"
                  v-model="filterModel.value"
                  :options="filterOptionsByField[column.field]"
                  filter
                  show-clear
                  :placeholder="columnFilterConfig[column.field].placeholder"
                  class="p-column-filter"
                  style="min-width: 6rem; max-width: 12rem"
                  @change="filterCallback()"
                />
                <div v-else-if="filterTypeFor(column.field) === 'number'" v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 0)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Min"
                      style="min-width: 3.5rem; max-width: 3.5rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 0, value, filterCallback)"
                    />
                    <InputText
                      :model-value="numericBetweenValue(filterModel, 1)"
                      type="text"
                      inputmode="numeric"
                      class="p-column-filter"
                      placeholder="Max"
                      style="min-width: 3.5rem; max-width: 3.5rem"
                      @update:model-value="value => onNumericBetweenFilterInput(filterModel, 1, value, filterCallback)"
                    />
                  </template>
                  <InputText
                    v-else
                    :model-value="numericFilterValue(filterModel)"
                    type="text"
                    inputmode="numeric"
                    class="p-column-filter"
                    :placeholder="columnFilterConfig[column.field].placeholder"
                    style="min-width: 4.5rem; max-width: 4.5rem"
                    @update:model-value="value => onNumericFilterInput(filterModel, value, filterCallback)"
                  />
                </div>
                <div v-else-if="filterTypeFor(column.field) === 'date'" v-show="showFilters" class="flex gap-1">
                  <template v-if="filterModel.matchMode === FilterMatchMode.BETWEEN">
                    <InputText
                      type="date"
                      :model-value="dateBetweenValue(filterModel, 0)"
                      class="p-column-filter"
                      style="min-width: 8rem; max-width: 8rem"
                      @update:model-value="value => onDateBetweenFilterInput(filterModel, 0, value, filterCallback, columnFilterConfig[column.field].dateUpperBoundSuffix)"
                    />
                    <InputText
                      type="date"
                      :model-value="dateBetweenValue(filterModel, 1)"
                      class="p-column-filter"
                      style="min-width: 8rem; max-width: 8rem"
                      @update:model-value="value => onDateBetweenFilterInput(filterModel, 1, value, filterCallback, columnFilterConfig[column.field].dateUpperBoundSuffix)"
                    />
                  </template>
                  <InputText
                    v-else-if="filterModel.matchMode === FilterMatchMode.CONTAINS"
                    v-model="filterModel.value"
                    type="text"
                    class="p-column-filter"
                    :placeholder="columnFilterConfig[column.field].placeholder"
                    style="min-width: 7rem; max-width: 7rem"
                    @input="filterCallback()"
                  />
                  <InputText
                    v-else
                    type="date"
                    :model-value="dateFilterValue(filterModel)"
                    class="p-column-filter"
                    style="min-width: 8rem; max-width: 8rem"
                    @update:model-value="value => onDateFilterInput(filterModel, value, filterCallback, columnFilterConfig[column.field].dateUpperBoundSuffix)"
                  />
                </div>
                <InputText
                  v-else
                  v-show="showFilters"
                  v-model="filterModel.value"
                  type="text"
                  class="p-column-filter"
                  :placeholder="columnFilterConfig[column.field]?.placeholder"
                  style="min-width: 8rem; max-width: 12rem"
                  @input="filterCallback()"
                />
              </template>
              <template #body="slotProps">
                <span v-if="column.field === 'advertiser'" v-tooltip.top="slotProps.data.advertiser" class="block overflow-hidden whitespace-nowrap font-semibold">{{ slotProps.data.advertiser }}</span>
                <span v-else-if="column.field === 'holdCount'" :class="{ 'text-red-600': slotProps.data.hasHoldMismatch }">{{ slotProps.data.holdCount }}</span>
                <span v-else-if="column.field === 'faces'" v-tooltip.top="slotProps.data.faces" class="block overflow-hidden whitespace-nowrap">
                  <template v-for="(face, index) in slotProps.data.faceDetails" :key="`${slotProps.data.proposalId}-${face.invNumber}-${index}`">
                    <span v-if="index > 0">, </span>
                    <span :class="{ 'text-red-600': face.isMismatched }">{{ face.isMismatched ? `${face.invNumber} (${face.offerClass})` : face.invNumber }}</span>
                  </template>
                </span>
                <span v-else-if="column.field === 'opportunityName'" v-tooltip.top="slotProps.data.opportunityName" class="block overflow-hidden whitespace-nowrap">{{ slotProps.data.opportunityName }}</span>
                <span v-else>{{ slotProps.data[column.field] }}</span>
              </template>
            </Column>
            <Column
              class="w-1/12"
              :reorderable-column="false"
              frozen
              align-frozen="right"
            >
              <template #body="slotProps">
                <Button
                  v-tooltip.left="'Maintenance'"
                  size="small"
                  rounded
                  class="icon-button"
                  style="background:none; border:none; color:var(--primary-blue)"
                  @click.stop="event => onMaintenanceClick(slotProps.data, event)"
                >
                  <EllipsisHIcon size="1.5rem" color="var(--apx-dark-gray-400)" />
                </Button>
              </template>
            </Column>
          </DataTable>
        </template>
      </HeightOptimizer>
    </template>
    <OfferHoldDialog
      :visible="showHoldDialog"
      :row="selectedOffer ?? undefined"
      :can-edit="settings.canEditHoldThru"
      :is-saving="isSavingHoldThru"
      @close="closeHoldDialog"
      @on:save="onSaveHoldThru"
      @on:open-opportunity="openOpportunityFlyout"
    />
    <OfferHoldHistoryDialog
      :visible="showHistoryDialog"
      :row="selectedOffer ?? undefined"
      :history="holdHistory"
      :is-loading="isLoadingHoldHistory"
      @close="closeHistoryDialog"
    />
    <Menu ref="maintenanceMenuRef" :model="maintenanceMenuOptions" :popup="true" />
  </AppContainer>
  <RouterView v-if="isKeepAliveActive" />
</template>

<style scoped>
.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}

.p-badge-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
